import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { persistReportId } from '../system/report-id-storage';
import { installSessionCookieBridge } from '../system/session-persistence';
import { AppVarsContext } from '../context/AppVarsContext';
import ReportAccessPage from './ReportAccessPage';
import ContactRep from '../system/ContactRep';
import { buildReportAccessHeaders, clearReportSession, getReportAccessToken, saveReportAccessToken } from '../system/report-access';

export default function GoLinkSetter({ requestedSlug, onAccessGranted }) {
    const { pathname, search } = useLocation();
    const { appVars, appLoading } = useContext(AppVarsContext) || {};
    const apiUrl = (appVars?.api_url || 'https://api.digitalwerks.com').replace(/\/$/, '');
    const query = useMemo(() => new URLSearchParams(search), [search]);
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');
    const [entryDates] = useState(() => ({ start: sessionStorage.getItem('sDate'), end: sessionStorage.getItem('eDate') }));
    const [attempt, setAttempt] = useState(0);
    const [password, setPassword] = useState('');
    const [submission, setSubmission] = useState(null);
    const [deniedProfile, setDeniedProfile] = useState(null);
    const [passwordError, setPasswordError] = useState('');
    const slug = useMemo(() => {
        if (requestedSlug) return requestedSlug;
        try { return decodeURIComponent(pathname.split('/')[2] || ''); }
        catch { return ''; }
    }, [pathname, requestedSlug]);

    useEffect(() => {
        if (appLoading) return undefined;
        let cancelled = false;
        setStatus('loading');
        // Clear the previous report once per lookup, rather than on every render.
        installSessionCookieBridge();
        clearReportSession();
        if (!slug || !/^[^/?#\s]+$/.test(slug) || (!requestedSlug && pathname.split('/').slice(3).some(Boolean))) {
            setStatus('not-found');
            return undefined;
        }

        (async () => {
            try {
                const res = await fetch(`${apiUrl}/reportprofile/slug`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', ...(getReportAccessToken(slug) ? { 'X-Report-Access': getReportAccessToken(slug) } : {}) }, body: JSON.stringify({ slug, ...(submission !== null ? { password: submission.password } : {}) }),
                });
                if (cancelled) return;
                if (res.status === 401 || res.status === 403) {
                    const access = await res.json();
                    if (cancelled) return;
                    if (access?.error?.code === 'report_password_required') {
                        setPassword(''); setStatus('password'); return;
                    }
                    if (access?.error?.code === 'report_password_denied') {
                        saveReportAccessToken(slug, ''); setDeniedProfile(access.accessProfile); setPassword(''); setStatus('denied'); return;
                    }
                }
                if (res.status === 429) {
                    setPasswordError('Too many attempts. Please wait 15 minutes before trying again.');
                    setPassword(''); setStatus('password'); return;
                }
                if (res.status === 404 || res.status === 410) {
                    setStatus('not-found');
                    return;
                }
                if (!res.ok) throw new Error('Report lookup unavailable');
                const data = await res.json();
                if (cancelled) return;
                const rid = data?.clientid;
                // Unknown slugs return false with HTTP 200. Never request /tagmanager/undefined.
                if (typeof rid !== 'string' || !rid.trim()) {
                    setStatus('not-found');
                    return;
                }
                saveReportAccessToken(slug, data.accessToken);
                sessionStorage.setItem('reportSlug', slug);
                const sDate = query.get('s') || data.sDate || (requestedSlug ? entryDates.start : '');
                const eDate = query.get('e') || data.eDate || (requestedSlug ? entryDates.end : '');
                const rdQS = query.get('rd') || (requestedSlug && !pathname.startsWith('/session/') ? `${pathname}${search}` : '');
                const tagManagerRes = await fetch(`${apiUrl}/tagmanager/${encodeURIComponent(rid)}`, { headers: buildReportAccessHeaders(slug) });
                if (!tagManagerRes.ok) {
                    const access = await tagManagerRes.json().catch(() => ({}));
                    if (cancelled) return;
                    if (access?.error?.code === 'report_password_required') {
                        saveReportAccessToken(slug, '');
                        setPassword(''); setStatus('password'); return;
                    }
                    throw new Error('Report setup unavailable');
                }
                const tagManagerData = await tagManagerRes.json();
                if (cancelled) return;
                if (!tagManagerData || typeof tagManagerData !== 'object') throw new Error('Report setup unavailable');

                persistReportId(rid);
                sessionStorage.setItem('reportSlug', slug);
                sessionStorage.setItem('client', JSON.stringify(tagManagerData));
                sessionStorage.setItem('apiID', tagManagerData.client);
                sessionStorage.setItem('aPort', tagManagerData.aPort);
                sessionStorage.setItem('agencyName', tagManagerData.clientname);
                sessionStorage.setItem('clientName', tagManagerData.name);
                if (sDate) sessionStorage.setItem('sDate', sDate);
                if (eDate) sessionStorage.setItem('eDate', eDate);

                const nextQuery = new URLSearchParams();
                if (sDate) nextQuery.set('s', sDate);
                if (eDate) nextQuery.set('e', eDate);
                if (rdQS.startsWith('/')) nextQuery.set('rd', rdQS);
                navigate({ pathname: `/session/${rid}`, search: nextQuery.toString() ? `?${nextQuery}` : '' }, { replace: true });
                onAccessGranted?.();
            } catch {
                if (!cancelled) setStatus('unavailable');
            }
        })();
        return () => { cancelled = true; };
    }, [slug, pathname, navigate, query, attempt, submission, requestedSlug, search, onAccessGranted, entryDates, apiUrl, appLoading]);

    if (status === 'denied') return <ContactRep accessDenied accessProfile={deniedProfile} />;
    if (status === 'password') return <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#F8FAFC', p: 2, boxSizing: 'border-box' }}>
        <Paper component="form" onSubmit={event => { event.preventDefault(); if (password) { setPasswordError(''); setSubmission({ password }); setPassword(''); } }} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 420, boxSizing: 'border-box' }}>
            <Typography component="h1" variant="h5" fontWeight={700}>Enter your report password</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>Enter the password shared by your account team. Access will be remembered in this tab.</Typography>
            <TextField autoFocus fullWidth required type="password" label="Report password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} error={Boolean(passwordError)} helperText={passwordError || ' '} />
            <Button type="submit" variant="contained" fullWidth disabled={!password} sx={{ mt: 1, minHeight: 44 }}>Open report</Button>
        </Paper>
    </Box>;
    if (status !== 'loading') return <ReportAccessPage key={pathname} slug={slug} unavailable={status === 'unavailable'} onRetry={() => setAttempt((value) => value + 1)} />;
    return <Box role="status" sx={{ minHeight: '100dvh', display: 'grid', placeContent: 'center', justifyItems: 'center', gap: 2, p: 3, bgcolor: '#F8FAFC', color: '#475569' }}>
        <CircularProgress size={28} />
        <Typography>Opening your report…</Typography>
    </Box>;
}
